import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { filter, map, startWith, distinctUntilChanged, shareReplay } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IconComponent } from '@design-system/buttons/icon/icon.component';
import { NavGroup, NavAction } from '../models/nav-actions.model';
import { H5Component } from '@design-system/typography/h5/h5.component';
import { BodyMediumComponent } from '@design-system/typography/body-md/body-medium.component';
import { TestIdDirective } from '@design-system/core/test-id.directive';
import { AnchorComponent } from '@design-system/buttons/anchor/anchor.component';
import { getUrlPath } from '@utils/string-utils';

@Component({
  selector: 'app-sub-nav',
  templateUrl: './sub-nav.component.html',
  styleUrls: ['./sub-nav.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    IconComponent,
    H5Component,
    BodyMediumComponent,
    TestIdDirective,
    AnchorComponent,
  ],
})
export class SubNavComponent implements OnChanges {
  @Input() groups: NavGroup[] | null = null;
  @Input() parentTitle?: string;

  /**
   * Observable of the current URL path (without query params/fragments)
   * Emits whenever navigation ends, optimized with distinctUntilChanged and shareReplay
   */
  currentUrl$: Observable<string>;

  private readonly destroyRef = inject(DestroyRef);

  rowHeight = 44;
  /** Collapsible groups: open only when true (synced from URL or user accordion toggle). */
  expandedGroups: Record<number, boolean> = {};

  /**
   * When the user collapses a group that still contains the active route, we keep it closed
   * until the URL leaves that group (or `groups` is replaced).
   */
  private suppressedAutoExpand: Record<number, true> = {};

  constructor(
    public router: Router,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.currentUrl$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => getUrlPath(this.router.url)),
      startWith(getUrlPath(this.router.url)),
      distinctUntilChanged(),
      shareReplay(1),
    );

    this.currentUrl$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.applyExpandedStateForUrl(getUrlPath(this.router.url));
      this.cdr.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['groups'] && this.groups) {
      this.suppressedAutoExpand = {};
      this.applyExpandedStateForUrl(getUrlPath(this.router.url));
    }
  }

  /**
   * Checks if the given routerLink matches the current route
   * @param routerLink The route link to check
   * @param currentUrl The current URL path (without query params/fragments)
   * @returns True if the current route starts with the given routerLink
   */
  isRouteActive(routerLink: string | null | undefined, currentUrl: string): boolean {
    if (!routerLink) {
      return false;
    }
    const path = this.normalizeNavLink(currentUrl);
    const link = this.normalizeNavLink(routerLink);
    return path.startsWith(link);
  }

  /** Align with nav config: JSON may omit a leading slash while Router paths include it. */
  private normalizeNavLink(routerLink: string): string {
    const t = routerLink.trim();
    if (!t) {
      return '';
    }
    return t.startsWith('/') ? t : `/${t}`;
  }

  goToRoute(action: NavAction) {
    if (action.routerLink) {
      this.router.navigate([action.routerLink]);
    }
  }

  expandAction(action: NavAction) {
    action.expanded = !action.expanded;
  }

  parentClick(action: NavAction) {
    if (action.routerLink) {
      this.goToRoute(action);
    } else {
      this.expandAction(action);
    }
  }

  toggleGroup(index: number) {
    const groups = this.groups;
    if (!groups?.[index]) {
      return;
    }
    const group = groups[index];
    if (!this.isCollapsable(group)) {
      return;
    }

    const url = getUrlPath(this.router.url);
    const isOpen = !!this.expandedGroups[index];

    if (isOpen) {
      if (this.groupContainsActiveRoute(group, url)) {
        this.suppressedAutoExpand[index] = true;
      }
      this.expandedGroups = { ...this.expandedGroups, [index]: false };
      this.cdr.markForCheck();
      return;
    }

    delete this.suppressedAutoExpand[index];

    const next = { ...this.expandedGroups };
    groups.forEach((g, i) => {
      if (!this.isCollapsable(g)) {
        return;
      }
      if (i === index) {
        next[i] = true;
      } else if (this.groupContainsActiveRoute(g, url)) {
        next[i] = true;
      } else {
        next[i] = false;
      }
    });
    this.expandedGroups = next;
    this.cdr.markForCheck();
  }

  /** JSON may use boolean or string `"true"`. */
  isCollapsable(group: NavGroup): boolean {
    return group.collapsable === true || (group.collapsable as unknown) === 'true';
  }

  isGroupExpanded(index: number, group: NavGroup): boolean {
    if (!this.isCollapsable(group)) {
      return true;
    }
    return !!this.expandedGroups[index];
  }

  isGroupBodyVisible(index: number, group: NavGroup): boolean {
    if (!this.isCollapsable(group)) {
      return true;
    }
    return this.isGroupExpanded(index, group);
  }

  calculateRowHeight(action: NavGroup) {
    if (action.items !== undefined) {
      return action?.items?.length * this.rowHeight;
    }

    return 0;
  }

  convertTitleToTestId(title: string): string {
    return title.toLowerCase().replaceAll(' ', '-') + '-sub-nav-link';
  }

  private applyExpandedStateForUrl(currentUrl: string): void {
    const groups = this.groups ?? [];
    const next = { ...this.expandedGroups };
    groups.forEach((g, i) => {
      if (!this.isCollapsable(g)) {
        return;
      }
      const containsActive = this.groupContainsActiveRoute(g, currentUrl);
      if (!containsActive) {
        delete this.suppressedAutoExpand[i];
        next[i] = false;
        return;
      }
      if (this.suppressedAutoExpand[i]) {
        next[i] = !!this.expandedGroups[i];
        return;
      }
      next[i] = true;
    });
    this.expandedGroups = next;
  }

  private groupContainsActiveRoute(group: NavGroup, currentUrl: string): boolean {
    return group.items?.some((item) => this.isRouteActive(item.routerLink, currentUrl)) ?? false;
  }
}
